/**
 * Sub-section moves on a form skeleton (sections → subSections), mirroring the form builder:
 * a move is `{ kind: 'section', sectionId, parentId, prevSectionId }` and places the section under
 * `parentId` after `prevSectionId` (0 = first), which is the body of the form builder's section
 * reorder API (`{ sectionId: parentId, prevSectionId }`).
 */
import { findSkeletonSection } from "./questionMoveUtils";

export const SECTION_MOVE_KIND = 'section';

/** @returns {number|null} id of the section that holds this sub-section, 0 for a top-level section */
export const findParentSectionId = (sections, sectionId, parentId = 0) => {
	for (const section of sections ?? []) {
		if (section.id === sectionId) return parentId;
		const nested = findParentSectionId(section.subSections, sectionId, section.id);
		if (nested != null) return nested;
	}
	return null;
};

/** True when `sectionId` is `ancestorId` itself or sits anywhere below it */
export const isSameOrDescendantSection = (sections, ancestorId, sectionId) => {
	const ancestor = findSkeletonSection(sections, ancestorId);
	return !!ancestor && (ancestor.id === sectionId || !!findSkeletonSection(ancestor.subSections, sectionId));
};

const getChildList = (skeleton, parentId) =>
	parentId ? findSkeletonSection(skeleton.sections, parentId)?.subSections : skeleton.sections;

const removeSection = (sections, sectionId) => {
	for (const section of sections ?? []) {
		const index = section.subSections?.findIndex(child => child.id === sectionId) ?? -1;
		if (index >= 0) return section.subSections.splice(index, 1)[0];
		const nested = removeSection(section.subSections, sectionId);
		if (nested) return nested;
	}
	return null;
};

/**
 * Applies one move to a copy of the skeleton; the section keeps its questions and nested sub-sections.
 * @returns {object|null} the new skeleton, or null when the section or target parent no longer exists
 */
export const applySectionMove = (skeleton, { sectionId, parentId, prevSectionId }) => {
	const next = JSON.parse(JSON.stringify(skeleton));
	if (!parentId || !findSkeletonSection(next.sections, parentId)) return null;
	if (isSameOrDescendantSection(next.sections, sectionId, parentId)) return null;

	const moved = removeSection(next.sections, sectionId);
	if (!moved) return null;

	const parent = findSkeletonSection(next.sections, parentId);
	parent.subSections = parent.subSections ?? [];
	const prevIndex = prevSectionId ? parent.subSections.findIndex(child => child.id === prevSectionId) : -1;
	// An anchor that has gone away falls back to the end of the list
	const insertIndex = !prevSectionId ? 0 : (prevIndex >= 0 ? prevIndex + 1 : parent.subSections.length);
	parent.subSections.splice(insertIndex, 0, moved);
	return next;
};

/**
 * Builds the move for dropping a sub-section next to another sub-section ('before' | 'after') or onto a
 * section ('inside', appended as its last sub-section). Returns null when the drop is not allowed
 * (onto itself or its own sub-sections, or a top-level target for 'before'/'after') or changes nothing.
 */
export const buildSectionMove = (skeleton, sectionId, anchorSectionId, position) => {
	const sections = skeleton?.sections;
	if (!sectionId || !anchorSectionId || isSameOrDescendantSection(sections, sectionId, anchorSectionId)) return null;

	const parentId = position === 'inside' ? anchorSectionId : findParentSectionId(sections, anchorSectionId);
	if (!parentId) return null;

	const siblings = (getChildList(skeleton, parentId) ?? []).filter(child => child.id !== sectionId);
	let insertIndex = siblings.length;
	if (position !== 'inside') {
		const anchorIndex = siblings.findIndex(child => child.id === anchorSectionId);
		if (anchorIndex >= 0) insertIndex = position === 'before' ? anchorIndex : anchorIndex + 1;
	}
	const prevSectionId = insertIndex > 0 ? siblings[insertIndex - 1].id : 0;

	const currentParentId = findParentSectionId(sections, sectionId);
	const currentSiblings = getChildList(skeleton, currentParentId) ?? [];
	const currentIndex = currentSiblings.findIndex(child => child.id === sectionId);
	const currentPrevSectionId = currentIndex > 0 ? currentSiblings[currentIndex - 1].id : 0;
	if (currentParentId === parentId && prevSectionId === currentPrevSectionId) return null;

	return { kind: SECTION_MOVE_KIND, sectionId, parentId, prevSectionId };
};
